import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, formatApiErrorDetail } from "../lib/api";
import { AppHeader } from "../components/AppHeader";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import {
  ArrowLeft, Plus, Trash2, User, Briefcase, Send, Copy, CheckCircle2, MessageCircle, KeyRound,
} from "lucide-react";
import { toast } from "sonner";
import { buildWhatsAppUrl } from "../lib/phone";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";

export default function Employees() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", role: "", phone: "" });
  const [adding, setAdding] = useState(false);
  const [invite, setInvite] = useState(null); // {employee, code, accept_url}

  const refresh = () => {
    setLoading(true);
    apiClient.get("/employees").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(refresh, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.role.trim()) { toast.error("Name and role required"); return; }
    setAdding(true);
    try {
      const { data } = await apiClient.post("/employees", {
        name: form.name.trim(), role: form.role.trim(), phone: form.phone.trim(),
      });
      setItems((arr) => [data, ...arr]);
      setForm({ name: "", role: "", phone: "" });
      toast.success("Employee added");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setAdding(false); }
  };

  const remove = async (id) => {
    try { await apiClient.delete(`/employees/${id}`); setItems((arr) => arr.filter((e) => e.id !== id)); toast.success("Removed"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
  };

  const generateInvite = async (emp) => {
    try {
      const { data } = await apiClient.post(`/employees/${emp.id}/invite`);
      setInvite({ employee: emp, ...data });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const copyText = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); }
    catch { toast.error("Copy failed — long-press to copy"); }
  };

  const whatsappShare = () => {
    if (!invite) return;
    const msg = `Hi ${invite.employee.name}! Join KonexoCRM as ${invite.employee.role}. Use this link: ${invite.accept_url} (or paste code: ${invite.code})`;
    const url = buildWhatsAppUrl(invite.employee.phone, msg);
    if (!url) { toast.error("This employee's phone number looks invalid"); return; }
    window.open(url, "_blank");
  };

  return (
    <>
      <AppHeader title="Employees" subtitle="Your team" />
      <div className="px-4 pt-4 pb-28 space-y-4">
        <button onClick={() => navigate(-1)} data-testid="employees-back" className="flex items-center gap-1 text-sm font-semibold text-slate-600">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <form onSubmit={add} data-testid="employee-form" className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3">
          <h3 className="text-base font-bold text-slate-900">Add new staff</h3>
          <div className="space-y-1.5">
            <Label>Name *</Label>
            <Input data-testid="emp-input-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Raju" required className="h-12 rounded-xl text-base" />
          </div>
          <div className="space-y-1.5">
            <Label>Role *</Label>
            <Input data-testid="emp-input-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="e.g., Delivery Boy" required className="h-12 rounded-xl text-base" />
          </div>
          <div className="space-y-1.5">
            <Label>Phone (for WhatsApp invite)</Label>
            <Input data-testid="emp-input-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} type="tel" placeholder="Optional" className="h-12 rounded-xl text-base" />
          </div>
          <Button type="submit" disabled={adding} data-testid="emp-add-btn" className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold gap-2">
            <Plus className="w-4 h-4" /> {adding ? "Adding..." : "Add"}
          </Button>
        </form>

        <div className="space-y-2" data-testid="employees-list">
          {loading ? [1, 2].map((i) => <Skeleton key={i} className="h-20 rounded-2xl" />) :
            items.length === 0 ? (
              <div className="text-center py-6 bg-white rounded-2xl border border-dashed border-slate-200 text-sm text-slate-500">No staff added yet</div>
            ) : items.map((e) => (
              <div key={e.id} data-testid={`employee-${e.id}`} className="bg-white border border-slate-200 rounded-2xl p-3 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-indigo-100 text-indigo-700 grid place-items-center font-bold">{e.name.charAt(0).toUpperCase()}</div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 truncate flex items-center gap-1.5"><User className="w-3.5 h-3.5" />{e.name}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-1.5 flex-wrap">
                      <Briefcase className="w-3 h-3" />{e.role}{e.phone ? ` · ${e.phone}` : ""}
                      {e.has_account && (
                        <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full text-[10px] font-bold">
                          <CheckCircle2 className="w-3 h-3" /> Active
                        </span>
                      )}
                    </div>
                  </div>
                  <button onClick={() => remove(e.id)} data-testid={`employee-delete-${e.id}`} className="p-2 text-slate-400 hover:text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                {!e.has_account && (
                  <Button onClick={() => generateInvite(e)} data-testid={`emp-invite-${e.id}`}
                    variant="outline" className="w-full h-11 rounded-xl border-indigo-300 text-indigo-700 hover:bg-indigo-50 gap-2">
                    <Send className="w-4 h-4" /> Send invite link
                  </Button>
                )}
                {e.has_account && e.email && (
                  <div className="text-xs text-slate-500 flex items-center gap-1"><KeyRound className="w-3 h-3" /> Logs in with {e.email}</div>
                )}
              </div>
            ))
          }
        </div>
      </div>

      <Dialog open={!!invite} onOpenChange={(o) => !o && setInvite(null)}>
        <DialogContent className="max-w-sm rounded-2xl">
          <DialogHeader>
            <DialogTitle>Invite {invite?.employee.name}</DialogTitle>
            <DialogDescription>Share this link or code. Expires in 7 days.</DialogDescription>
          </DialogHeader>
          {invite && (
            <div className="space-y-3">
              <div className="bg-slate-50 rounded-xl p-3 text-xs font-mono break-all border border-slate-200" data-testid="invite-link">{invite.accept_url}</div>
              <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-xl p-3">
                <KeyRound className="w-4 h-4 text-indigo-700" />
                <span className="font-mono font-bold text-indigo-900 flex-1" data-testid="invite-code-display">{invite.code}</span>
                <button onClick={() => copyText(invite.code)} data-testid="copy-code-btn" className="text-indigo-700 hover:text-indigo-900"><Copy className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={() => copyText(invite.accept_url)} data-testid="copy-link-btn" variant="outline" className="h-12 rounded-xl gap-2">
                  <Copy className="w-4 h-4" /> Copy link
                </Button>
                <Button onClick={whatsappShare} data-testid="whatsapp-share-btn"
                  className="h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white gap-2">
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setInvite(null)} data-testid="close-invite-dialog" className="rounded-xl">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
